import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
	Modal,
	ModalTrigger,
	ModalContent,
	ModalHeader,
	ModalTitle,
	ModalDescription,
	ModalFooter,
	ModalClose,
	Button,
	FormField,
	CardContent,
	Alert,
	PlatformMark,
} from '../ui';
import { useAccountCreate } from '../features/clients';
import { useData } from '../../contexts/DataContext';
import ScanService from '../../services/ScanService';

/**
 * AddAccountForm - pick a network, type a handle, done.
 *
 * Two things were wrong with the previous version and both were user-visible:
 *
 * 1. The platform `<select>` carried a hardcoded `disabled` attribute, so it
 *    was permanently stuck on its default of 'X'. Adding a TikTok account was
 *    literally impossible through the UI no matter what the options contained.
 *    The reset then set it to 'Instagram', a platform that isn't enabled, so
 *    the control could end up matching no option at all.
 * 2. It asked the person to hand-type bio, follower count and verified status -
 *    all of which the scraper fetches accurately on create, and all of which go
 *    stale the moment they're typed. Asking for data we already have is the
 *    "weird" part; comparable products ask for the network and the handle only.
 *
 * The onboarding and modal variants previously duplicated the entire form body,
 * which is exactly how BOTH copies of the select ended up disabled. There is now
 * one body rendered into two shells.
 */
export const AddAccountForm = forwardRef(function AddAccountForm({ trigger, clientId, clientName, onSuccess, isOnboarding = false, suggestedPlatform = null, isOpen: externalIsOpen, onOpenChange: externalOnOpenChange, onValidityChange }, ref) {
	const [internalIsOpen, setInternalIsOpen] = useState(false);

	// Use external state if provided, otherwise use internal state
	const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
	const setIsOpen = externalOnOpenChange || setInternalIsOpen;

	// Derived from the canonical list so this form cannot offer a platform with
	// no scraper adapter behind it. `short` matches the casing stored on
	// Account.platform ("X", "TikTok"), so no translation is needed on submit.
	const platformOptions = ScanService.getEnabledPlatforms().map((p) => ({
		value: p.short,
		label: p.label,
	}));

	// Default to a platform that actually exists in the list rather than a
	// hardcoded literal that may not be enabled.
	const defaultPlatform =
		(suggestedPlatform && platformOptions.some((p) => p.value === suggestedPlatform)
			? suggestedPlatform
			: platformOptions[0]?.value) || '';

	const emptyForm = { handle: '', platform: defaultPlatform, notes: '' };
	const [formData, setFormData] = useState(emptyForm);

	const { fetchClients } = useData();
	const { createAccount, loading, error, clearError } = useAccountCreate();

	const validateFormWithData = (data) => {
		if (!data.handle.trim()) {
			return { valid: false, error: 'Account handle is required' };
		}
		if (!data.platform) {
			return { valid: false, error: 'Platform is required' };
		}
		return { valid: true };
	};

	const validateForm = () => validateFormWithData(formData);

	useEffect(() => {
		if (isOnboarding && onValidityChange) {
			onValidityChange(validateForm().valid);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOnboarding]);

	const handleInputChange = (field, value) => {
		// Auto-correct a leading "@" as users type it - a very common honest
		// mistake that otherwise stores "@handle" and breaks scans.
		if (field === 'handle' && typeof value === 'string') {
			value = value.replace(/^@+/, '');
		}

		const updated = { ...formData, [field]: value };
		setFormData(updated);
		if (error) clearError();

		if (isOnboarding && onValidityChange) {
			onValidityChange(validateFormWithData(updated).valid);
		}
	};

	const handleSubmit = async (e) => {
		if (e) e.preventDefault();
		if (!validateForm().valid) return;

		// Only the two things we cannot derive. Display name, bio, avatar,
		// follower counts and verified status are fetched from the platform on
		// create - see accountController's enrichment step.
		const result = await createAccount({
			handle: formData.handle.trim().replace(/^@+/, ''),
			platform: formData.platform,
			clientId,
			notes: formData.notes.trim(),
			// Skip the scraper fetch during onboarding to avoid a timeout there.
			isOnboarding,
		});

		if (result.success) {
			await fetchClients();
			if (!isOnboarding) {
				setIsOpen(false);
				setFormData(emptyForm);
			}
			if (onSuccess) onSuccess(result.data);
		}
	};

	const handleCancel = () => {
		setIsOpen(false);
		setFormData(emptyForm);
		clearError();
	};

	useImperativeHandle(ref, () => ({
		submit: handleSubmit,
		validate: validateForm,
	}));

	const selected = platformOptions.find((p) => p.value === formData.platform);
	const previewHandle = formData.handle.trim().replace(/^@+/, '');

	/**
	 * One body, two shells. Radio semantics rather than buttons so the group is
	 * keyboard- and screen-reader navigable.
	 */
	const body = (
		<div className="space-y-5">
			{error && <Alert variant="error">{error}</Alert>}

			<FormField label="Platform *" helpText="The networks we can scan today">
				<div
					role="radiogroup"
					aria-label="Platform"
					className="grid grid-cols-3 gap-2"
				>
					{platformOptions.map((option) => {
						const active = formData.platform === option.value;
						return (
							<button
								key={option.value}
								type="button"
								role="radio"
								aria-checked={active}
								onClick={() => handleInputChange('platform', option.value)}
								className={`flex flex-col items-center gap-2 rounded-xl border p-3 transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1 ${
									active
										? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600 dark:bg-brand-500/10'
										: 'border-border bg-card hover:border-input hover:bg-muted'
								}`}
							>
								<PlatformMark platform={option.value} size="md" />
								<span
									className={`text-xs font-medium ${
										active ? 'text-brand-700' : 'text-muted-foreground'
									}`}
								>
									{option.value}
								</span>
							</button>
						);
					})}
				</div>
			</FormField>

			<FormField
				label="Account Handle *"
				error={error && !formData.handle.trim() ? 'Handle is required' : undefined}
			>
				{/* The @ lives in the field furniture, so the value stored is always
				    the bare handle the scraper expects. */}
				<div className="flex items-stretch rounded-md border border-input focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-transparent overflow-hidden">
					<span className="flex items-center px-3 bg-muted text-muted-foreground text-sm border-r border-border select-none">
						@
					</span>
					<input
						className="flex-1 min-w-0 px-3 py-2 focus:outline-none"
						placeholder="username"
						value={formData.handle}
						onChange={(e) => handleInputChange('handle', e.target.value)}
						autoComplete="off"
						autoCapitalize="none"
						spellCheck={false}
						required
					/>
				</div>
				{previewHandle && selected && (
					<p className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
						<PlatformMark platform={formData.platform} size="xs" soft />
						Monitoring <span className="font-medium text-foreground">@{previewHandle}</span> on{' '}
						{selected.label}
					</p>
				)}
			</FormField>

			<FormField label="Notes" helpText="Optional - context for your team">
				<textarea
					className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
					rows={2}
					placeholder="Why you're monitoring this account…"
					value={formData.notes}
					onChange={(e) => handleInputChange('notes', e.target.value)}
				/>
			</FormField>

			<p className="text-xs text-muted-foreground">
				We'll pull the display name, bio, avatar and follower count from the
				platform automatically.
			</p>
		</div>
	);

	if (isOnboarding) {
		return <form onSubmit={handleSubmit}>{body}</form>;
	}

	return (
		<Modal open={isOpen} onOpenChange={setIsOpen}>
			{trigger && <ModalTrigger asChild>{trigger}</ModalTrigger>}

			<ModalContent className="max-w-md">
				<ModalHeader>
					<ModalTitle>Add account to monitor</ModalTitle>
					<ModalDescription>
						{clientName ? `Add an account for ${clientName}` : 'Add a social media account to monitor'}
					</ModalDescription>
				</ModalHeader>

				<form onSubmit={handleSubmit}>
					<CardContent className="space-y-4">{body}</CardContent>

					<ModalFooter>
						<ModalClose asChild>
							<Button variant="secondary" onClick={handleCancel}>
								Cancel
							</Button>
						</ModalClose>
						<Button type="submit" loading={loading}>
							Add Account
						</Button>
					</ModalFooter>
				</form>
			</ModalContent>
		</Modal>
	);
});

export default AddAccountForm;
